/**
 * Special Screen V2 Mappings
 *
 * Mappings for screen types with unique content structures:
 * - Carousel: slide title/subtitle
 * - ProgressSteps: stepper label
 * - Paywall: product labels, purchase button
 * - FlexibleLayout: no additional mappings (canvas only)
 * - WebEmbed: no additional mappings
 *
 * @module themes/mappers/mappingTablesV2
 */

import type { ThemeV2Mapping } from '../ThemeV2Mapper';
import type { StepKind } from '@segmently/schema-models/v2';
import { addAlpha50 } from './copyBlockMappings';

// ============================================================================
// StepKind Helpers
// ============================================================================

const isCarousel = (stepKind: StepKind) => stepKind === 'Carousel';
const isProgressSteps = (stepKind: StepKind) => stepKind === 'ProgressSteps';
const isPaywall = (stepKind: StepKind) => stepKind === 'Paywall';
const hasFeaturedMedia = (stepKind: StepKind) =>
  stepKind === 'ListMultiPick' ||
  stepKind === 'ListSinglePick' ||
  stepKind === 'GridMultiPick' ||
  stepKind === 'GridSinglePick' ||
  stepKind === 'WheelPicker' ||
  stepKind === 'Paywall';

// ============================================================================
// Special Mappings
// ============================================================================

/**
 * Special screen mappings.
 *
 * Carousel V2 paths:
 * - Slide title: carousel.items[].content.title.appearance.*
 * - Slide subtitle: carousel.items[].content.subtitle.appearance.*
 * - Slide detail: carousel.items[].content.detail.appearance.*
 * - Slide image: carousel.items[].content.image.appearance.* and layout.insets
 * - Image/text containers: carousel.imageContainer / carousel.textContainer
 *
 * ProgressSteps V2 paths:
 * - Stepper label: stepper.label.appearance.*
 * - Segment title: stepper.items[].content.title.appearance.*
 *
 * Paywall V2 paths:
 * - Purchase button: paywall.purchaseAction.appearance.backgroundColor
 * - Purchase button text: paywall.purchaseAction.content.appearance.color
 * - Close button color: paywall.closeButton.content.appearance.color
 * - Product labels: paywall.products.items[].topStartLabel.appearance.*
 */
export const SPECIAL_V2_MAPPINGS: ThemeV2Mapping[] = [
  // ============================================================================
  // Carousel
  // ============================================================================

  // Slide title color
  {
    source: 'colors.titlesSubtitles',
    target: 'carousel.items[].content.title.appearance.color',
    condition: isCarousel,
  },
  // Slide title font
  {
    source: 'fonts.main.fontFamily',
    target: 'carousel.items[].content.title.appearance.fontFamily',
    condition: isCarousel,
  },
  // Slide subtitle color (50% alpha)
  {
    source: 'colors.titlesSubtitles',
    target: 'carousel.items[].content.subtitle.appearance.color',
    transform: addAlpha50,
    condition: isCarousel,
  },
  // Slide subtitle font
  {
    source: 'fonts.main.fontFamily',
    target: 'carousel.items[].content.subtitle.appearance.fontFamily',
    condition: isCarousel,
  },
  // Slide detail color (50% alpha)
  {
    source: 'colors.titlesSubtitles',
    target: 'carousel.items[].content.detail.appearance.color',
    transform: addAlpha50,
    condition: isCarousel,
  },
  // Slide detail font
  {
    source: 'fonts.main.fontFamily',
    target: 'carousel.items[].content.detail.appearance.fontFamily',
    condition: isCarousel,
  },
  // Slide text padding passthrough targets (template-sourced apply)
  {
    source: '__templateOnly',
    target: 'carousel.items[].content.title.layout.insets',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.items[].content.subtitle.layout.insets',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.items[].content.detail.layout.insets',
    condition: isCarousel,
  },
  // Slide image appearance passthrough targets (template-sourced apply)
  {
    source: '__templateOnly',
    target: 'carousel.items[].content.image.appearance.dimensions.width',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.items[].content.image.appearance.dimensions.height',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.items[].content.image.appearance.scaleMode',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.items[].content.image.appearance.cornerRadii.uniform',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.items[].content.image.layout.insets',
    condition: isCarousel,
  },
  // Carousel container layout passthrough targets (template-sourced apply)
  {
    source: '__templateOnly',
    target: 'carousel.imageContainer.verticalAlignment',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.imageContainer.heightKind',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.imageContainer.heightValue',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.imageContainer.insets',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.textContainer.verticalAlignment',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.textContainer.heightKind',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.textContainer.heightValue',
    condition: isCarousel,
  },
  {
    source: '__templateOnly',
    target: 'carousel.textContainer.insets',
    condition: isCarousel,
  },

  // === Carousel per-role fonts (last-defined override over fonts.main / palette) ===
  ...(['fontFamily', 'fontSize', 'fontWeight', 'align', 'color'] as const).map((key) => ({
    source: `fonts.title.${key}`,
    target: `carousel.items[].content.title.appearance.${key}`,
    condition: isCarousel,
  })),
  ...(['subtitle', 'detail'] as const).flatMap((slot) =>
    (['fontFamily', 'fontSize', 'fontWeight', 'align', 'color'] as const).map((key) => ({
      source: `fonts.subtitle.${key}`,
      target: `carousel.items[].content.${slot}.appearance.${key}`,
      condition: isCarousel,
    })),
  ),

  // ============================================================================
  // ProgressSteps
  // ============================================================================

  // Stepper label color
  {
    source: 'colors.titlesSubtitles',
    target: 'stepper.label.appearance.color',
    condition: isProgressSteps,
  },
  // Stepper label font
  {
    source: 'fonts.main.fontFamily',
    target: 'stepper.label.appearance.fontFamily',
    condition: isProgressSteps,
  },
  // Segment title color
  {
    source: 'colors.titlesSubtitles',
    target: 'stepper.items[].content.title.appearance.color',
    condition: isProgressSteps,
  },
  // Segment title font
  {
    source: 'fonts.main.fontFamily',
    target: 'stepper.items[].content.title.appearance.fontFamily',
    condition: isProgressSteps,
  },
  // Progress indicator accent/fill color = buttonsBackground
  {
    source: 'colors.buttonsBackground',
    target: 'stepper.appearance.fillColor',
    condition: isProgressSteps,
  },
  {
    source: 'layoutSettings.progress.indicatorHeight',
    target: 'stepper.appearance.thickness',
    condition: isProgressSteps,
  },
  {
    source: 'layoutSettings.progress.trackHeight',
    target: 'stepper.appearance.trackThickness',
    condition: isProgressSteps,
  },
  {
    source: 'layoutSettings.progress.containerInsets',
    target: 'stepper.layout.insets',
    condition: isProgressSteps,
  },

  // === Stepper per-role fonts (last-defined override over fonts.main / palette) ===
  ...(['fontFamily', 'fontSize', 'fontWeight', 'align', 'color'] as const).map((key) => ({
    source: `fonts.title.${key}`,
    target: `stepper.items[].content.title.appearance.${key}`,
    condition: isProgressSteps,
  })),
  ...(['label', 'items[].content.subtitle', 'items[].content.description'] as const).flatMap((slot) =>
    (['fontFamily', 'fontSize', 'fontWeight', 'align', 'color'] as const).map((key) => ({
      source: `fonts.subtitle.${key}`,
      target: `stepper.${slot}.appearance.${key}`,
      condition: isProgressSteps,
    })),
  ),

  // ============================================================================
  // Paywall
  // ============================================================================

  // Purchase button background = buttonsBackground
  {
    source: 'colors.buttonsBackground',
    target: 'paywall.purchaseAction.appearance.backgroundColor',
    condition: isPaywall,
  },
  // Purchase button text = buttonsTitle
  {
    source: 'colors.buttonsTitle',
    target: 'paywall.purchaseAction.content.appearance.color',
    condition: isPaywall,
  },
  // Purchase button font
  {
    source: 'fonts.main.fontFamily',
    target: 'paywall.purchaseAction.content.appearance.fontFamily',
    condition: isPaywall,
  },
  // Close button color = titlesSubtitles
  {
    source: 'colors.titlesSubtitles',
    target: 'paywall.closeButton.content.appearance.color',
    condition: isPaywall,
  },
  // Product topStart label color
  {
    source: 'colors.titlesSubtitles',
    target: 'paywall.products.items[].topStartLabel.appearance.color',
    condition: isPaywall,
  },
  // Product topStart label font
  {
    source: 'fonts.main.fontFamily',
    target: 'paywall.products.items[].topStartLabel.appearance.fontFamily',
    condition: isPaywall,
  },
  // Product bottomStart label color (50% alpha)
  {
    source: 'colors.titlesSubtitles',
    target: 'paywall.products.items[].bottomStartLabel.appearance.color',
    transform: addAlpha50,
    condition: isPaywall,
  },
  // Product bottomStart label font
  {
    source: 'fonts.main.fontFamily',
    target: 'paywall.products.items[].bottomStartLabel.appearance.fontFamily',
    condition: isPaywall,
  },
  // Auto-renew label color
  {
    source: 'colors.titlesSubtitles',
    target: 'paywall.autoRenewLabel.appearance.color',
    transform: addAlpha50,
    condition: isPaywall,
  },
  // Auto-renew label font
  {
    source: 'fonts.main.fontFamily',
    target: 'paywall.autoRenewLabel.appearance.fontFamily',
    condition: isPaywall,
  },

  // Product cell selected border color = buttonsBackground
  {
    source: 'colors.buttonsBackground',
    target: 'paywall.products.selectedAppearance.borders.color',
    condition: isPaywall,
  },

  // ============================================================================
  // Renderer-backed layout geometry
  // ============================================================================

  // Featured media height/radius for screens that expose featuredMedia
  {
    source: 'layoutSettings.media.featuredHeightPercentage',
    target: 'featuredMedia.appearance.dimensions.heightPercentage',
    condition: hasFeaturedMedia,
  },
  {
    source: 'layoutSettings.media.cornerRadius',
    target: 'featuredMedia.appearance.cornerRadii.uniform',
    condition: hasFeaturedMedia,
  },

  // Paywall purchase CTA mirrors the primary action button geometry
  {
    source: 'layoutSettings.actionBar.primaryButton.radius',
    target: 'paywall.purchaseAction.appearance.borders.radius.uniform',
    condition: isPaywall,
  },
  {
    source: 'layoutSettings.actionBar.primaryButton.height',
    target: 'paywall.purchaseAction.appearance.dimensions.height',
    condition: isPaywall,
  },
  {
    source: 'layoutSettings.actionBar.primaryButton.insets',
    target: 'paywall.purchaseAction.appearance.insets',
    condition: isPaywall,
  },

  // Paywall product cells and footer
  {
    source: 'layoutSettings.paywall.productRadius',
    target: 'paywall.products.appearance.borders.radius.uniform',
    condition: isPaywall,
  },
  {
    source: 'layoutSettings.paywall.productRadius',
    target: 'paywall.products.selectedAppearance.borders.radius.uniform',
    condition: isPaywall,
  },
  {
    source: 'layoutSettings.paywall.productInsets',
    target: 'paywall.products.appearance.insets',
    condition: isPaywall,
  },
  {
    source: 'layoutSettings.paywall.productSpacing',
    target: 'paywall.products.appearance.itemsSpacing',
    condition: isPaywall,
  },
  {
    source: 'layoutSettings.paywall.footerInsets',
    target: 'paywall.footerAppearance.insets',
    condition: isPaywall,
  },

  // FlexibleLayout container and section defaults
  {
    source: 'layoutSettings.flexibleLayout.containerInsets',
    target: 'flexibleLayout.insets',
    condition: (stepKind) => stepKind === 'FlexibleLayout',
  },
  {
    source: 'layoutSettings.flexibleLayout.sectionPadding',
    target: 'flexibleLayout.sections[].layout.padding',
    condition: (stepKind) => stepKind === 'FlexibleLayout',
  },
  {
    source: 'layoutSettings.flexibleLayout.sectionRadius',
    target: 'flexibleLayout.sections[].layout.cornerRadius.uniform',
    condition: (stepKind) => stepKind === 'FlexibleLayout',
  },
  {
    source: 'layoutSettings.flexibleLayout.sectionSpacing',
    target: 'flexibleLayout.sections[].layout.margin.bottom',
    condition: (stepKind) => stepKind === 'FlexibleLayout',
  },
];
